import React, { useState, useEffect } from 'react';
import { 
  DollarSign, AlertTriangle, Clock, CheckCircle, Send, 
  User, Mail, Calendar, CreditCard, Ban, Eye, ChevronRight,
  MessageSquare, Euro, TrendingUp, AlertCircle, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const CommissionTracking = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Dialogs
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  
  // Payment form
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_method: 'bank_transfer',
    reference: '',
    notes: ''
  });
  
  // Message form
  const [messageData, setMessageData] = useState({
    subject: '',
    message: '',
    message_type: 'general'
  });

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    if (selectedVendor) {
      loadVendorDetails(selectedVendor.vendor_id);
    }
  }, [selectedVendor]);

  const loadOverview = async () => {
    try {
      const response = await axiosInstance.get('/commissions/overview');
      setOverview(response.data);
    } catch (error) {
      console.error('Error loading overview:', error);
      toast.error('Fehler beim Laden der Übersicht');
    } finally {
      setLoading(false);
    }
  };

  const loadVendorDetails = async (vendorId) => {
    setLoadingDetails(true);
    try {
      const response = await axiosInstance.get(`/commissions/vendor/${vendorId}`);
      setVendorDetails(response.data);
    } catch (error) {
      console.error('Error loading vendor details:', error);
      toast.error('Fehler beim Laden der Details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedVendor || !selectedPeriod) return;
    
    try {
      await axiosInstance.post(
        `/commissions/vendor/${selectedVendor.vendor_id}/record-payment?period_id=${selectedPeriod.id}`,
        paymentData
      );
      toast.success('Zahlung erfasst');
      setShowPaymentDialog(false);
      loadVendorDetails(selectedVendor.vendor_id);
      loadOverview();
      setPaymentData({ amount: 0, payment_method: 'bank_transfer', reference: '', notes: '' });
    } catch (error) {
      toast.error('Fehler beim Erfassen der Zahlung');
    }
  };

  const handleSendMessage = async () => {
    if (!selectedVendor) return;
    
    try {
      await axiosInstance.post(
        `/commissions/vendor/${selectedVendor.vendor_id}/send-message`,
        messageData
      );
      toast.success('Nachricht gesendet');
      setShowMessageDialog(false);
      loadVendorDetails(selectedVendor.vendor_id);
      setMessageData({ subject: '', message: '', message_type: 'general' });
    } catch (error) {
      toast.error('Fehler beim Senden der Nachricht');
    }
  };

  const handleSendReminder = async () => {
    if (!selectedVendor) return;
    
    try {
      await axiosInstance.post(`/commissions/vendor/${selectedVendor.vendor_id}/send-reminder`);
      toast.success('Zahlungserinnerung gesendet');
      loadVendorDetails(selectedVendor.vendor_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    }
  };

  const handleSuspendVendor = async () => {
    if (!selectedVendor) return;
    if (!confirm('Möchten Sie diesen Verkäufer wirklich sperren? Alle Produkte werden deaktiviert.')) return;
    
    try {
      await axiosInstance.delete(`/commissions/vendor/${selectedVendor.vendor_id}/remove`);
      toast.success('Verkäufer gesperrt');
      setSelectedVendor(null);
      setVendorDetails(null);
      loadOverview();
    } catch (error) {
      toast.error('Fehler beim Sperren');
    }
  };

  const handleDemoteToCustomer = async () => {
    if (!selectedVendor) return;
    if (!confirm(
      'Möchten Sie diesen Verkäufer zum Kunden herabstufen?\n\n' +
      '• Der Account bleibt erhalten\n' +
      '• Alle Produkte werden deaktiviert (nicht gelöscht)\n' +
      '• Sie können den Status später wiederherstellen'
    )) return;
    
    try {
      const response = await axiosInstance.post(
        `/vendors/admin/${selectedVendor.vendor_id}/demote-to-customer`,
        null,
        { params: { reason: 'Nichtzahlung der Provision' } }
      );
      toast.success(response.data.message);
      setSelectedVendor(null);
      setVendorDetails(null);
      loadOverview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Herabstufen');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Offen', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      partial: { label: 'Teilweise', color: 'bg-blue-100 text-blue-800', icon: DollarSign },
      paid: { label: 'Bezahlt', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      overdue: { label: 'Überfällig', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
      no_commission: { label: 'Keine', color: 'bg-gray-100 text-gray-600', icon: null }
    };
    const c = config[status] || config.pending;
    return (
      <Badge className={`${c.color} flex items-center gap-1`}>
        {c.icon && <c.icon className="h-3 w-3" />}
        {c.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const formatCurrency = (amount) => {
    return `${formatEUR((amount || 0))}`;
  };

  if (loading) {
    return <div className="p-8">Lade Daten...</div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }}>
      <AdminPageHeader 
        title="Provisions-Tracking"
        subtitle="Verfolgen Sie offene Provisionen und Zahlungen der Verkäufer"
        icon={Euro}
      />

      {/* Section: Stats - Goldbraune Überschrift */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
          <TrendingUp className="h-4 w-4 text-[#60B060]" />
        </div>
        <h2 className="text-base font-semibold text-white">Übersicht</h2>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Euro className="h-8 w-8 mx-auto mb-2 text-blue-500" />
            <div className="text-2xl font-bold">{formatCurrency(overview?.totals?.total_owed)}</div>
            <div className="text-sm text-gray-600">Offene Provisionen</div>
          </CardContent>
        </Card>
        <Card className={overview?.totals?.total_overdue > 0 ? 'border-red-400 border-2' : ''}>
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-500" />
            <div className="text-2xl font-bold text-red-600">{formatCurrency(overview?.totals?.total_overdue)}</div>
            <div className="text-sm text-gray-600">Überfällig</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <User className="h-8 w-8 mx-auto mb-2 text-orange-500" />
            <div className="text-2xl font-bold text-orange-600">{overview?.totals?.vendors_in_arrears || 0}</div>
            <div className="text-sm text-gray-600">Im Verzug</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-500" />
            <div className="text-2xl font-bold">{overview?.totals?.active_vendors || 0}</div>
            <div className="text-sm text-gray-600">Aktive Verkäufer</div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue Alert */}
      {overview?.totals?.vendors_in_arrears > 0 && (
        <Card className="mb-6 bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-red-800">
                <strong>{overview.totals.vendors_in_arrears}</strong> Verkäufer haben überfällige Provisionen 
                ({formatCurrency(overview.totals.total_overdue)} insgesamt)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendors List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Verkäufer</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-y-auto">
                {overview?.vendors?.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Keine aktiven Verkäufer</p>
                  </div>
                ) : (
                  overview?.vendors?.map((vendor) => (
                    <div
                      key={vendor.vendor_id}
                      onClick={() => setSelectedVendor(vendor)}
                      className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedVendor?.vendor_id === vendor.vendor_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{vendor.shop_name}</p>
                          <p className="text-sm text-gray-500">{vendor.name}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {vendor.is_overdue ? (
                          <Badge className="bg-red-100 text-red-800 text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {formatCurrency(vendor.total_overdue)} überfällig
                          </Badge>
                        ) : vendor.total_owed > 0 ? (
                          <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                            {formatCurrency(vendor.total_owed)} offen
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Aktuell
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vendor Details */}
        <div className="lg:col-span-2">
          {selectedVendor && vendorDetails ? (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{vendorDetails.vendor?.shop_name}</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {vendorDetails.vendor?.name} • {vendorDetails.vendor?.email}
                    </p>
                  </div>
                  <Badge variant="outline">{vendorDetails.vendor?.commission_rate}% Provision</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Offen</p>
                    <p className="text-xl font-bold">{formatCurrency(vendorDetails.summary?.total_owed)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Überfällig</p>
                    <p className={`text-xl font-bold ${vendorDetails.summary?.total_overdue > 0 ? 'text-red-600' : ''}`}>
                      {formatCurrency(vendorDetails.summary?.total_overdue)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Bezahlt</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(vendorDetails.summary?.total_paid)}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {vendorDetails.summary?.is_overdue && (
                    <Button variant="destructive" size="sm" onClick={handleSendReminder}>
                      <Send className="h-4 w-4 mr-2" />
                      Mahnung senden
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setShowMessageDialog(true)} className="rounded-none bg-[#E3F2FD] text-[#2196F3] hover:bg-[#BBDEFB] border-0">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Nachricht senden
                  </Button>
                  <Button 
                    size="sm" 
                    className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                    onClick={handleSuspendVendor}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    Verkäufer sperren
                  </Button>
                  <Button 
                    size="sm" 
                    className="rounded-none bg-[#FFF3E0] text-[#FF9800] hover:bg-[#FFE0B2] border-0"
                    onClick={handleDemoteToCustomer}
                    title="Verkäufer wird zum normalen Kunden. Produkte bleiben erhalten (deaktiviert). Kann später wiederhergestellt werden."
                  >
                    <User className="h-4 w-4 mr-2" />
                    Zum Kunden herabstufen
                  </Button>
                </div>

                {/* Periods */}
                <Tabs defaultValue="periods">
                  <TabsList className="mb-4 bg-[#C4B5A4] p-1 rounded-none border-0 gap-1 inline-flex">
                    <TabsTrigger value="periods" className="rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-4 py-2">Abrechnungsperioden</TabsTrigger>
                    <TabsTrigger value="messages" className="rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-4 py-2">
                      Nachrichten
                      {vendorDetails.messages?.length > 0 && (
                        <Badge className="ml-2 bg-blue-500">{vendorDetails.messages.length}</Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="periods">
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {vendorDetails.periods?.filter(p => p.status !== 'no_commission').map((period) => (
                        <div key={period.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{period.period_label}</p>
                              <p className="text-sm text-gray-500">
                                {period.order_count} Bestellungen • {formatCurrency(period.total_sales)} Umsatz
                              </p>
                            </div>
                            {getStatusBadge(period.status)}
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 text-sm mt-3">
                            <div>
                              <span className="text-gray-500">Provision:</span>
                              <p className="font-medium">{formatCurrency(period.commission_amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Bezahlt:</span>
                              <p className="font-medium text-gray-900">{formatCurrency(period.amount_paid)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Offen:</span>
                              <p className={`font-medium ${period.amount_due > 0 ? 'text-red-600' : ''}`}>
                                {formatCurrency(period.amount_due)}
                              </p>
                            </div>
                          </div>

                          <div className="flex justify-between items-center mt-3 pt-3 border-t">
                            <p className="text-xs text-gray-400">
                              Fällig: {formatDate(period.due_date)}
                            </p>
                            {period.amount_due > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedPeriod(period);
                                  setPaymentData({ ...paymentData, amount: period.amount_due });
                                  setShowPaymentDialog(true);
                                }}
                              >
                                <CreditCard className="h-4 w-4 mr-2" />
                                Zahlung erfassen
                              </Button>
                            )}
                          </div>

                          {/* Payment History */}
                          {period.payments?.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-gray-500 mb-2">Zahlungen:</p>
                              {period.payments.map((payment, idx) => (
                                <div key={idx} className="text-xs bg-green-50 p-2 rounded mb-1">
                                  {formatCurrency(payment.amount)} - {formatDate(payment.recorded_at)}
                                  {payment.reference && <span className="text-gray-500"> (Ref: {payment.reference})</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="messages">
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {vendorDetails.messages?.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">Keine Nachrichten</p>
                      ) : (
                        vendorDetails.messages?.map((msg) => (
                          <div key={msg.id} className={`border rounded-lg p-4 ${
                            msg.message_type === 'reminder' ? 'bg-yellow-50 border-yellow-200' :
                            msg.message_type === 'warning' ? 'bg-orange-50 border-orange-200' :
                            msg.message_type === 'final_notice' ? 'bg-red-50 border-red-200' :
                            'bg-gray-50'
                          }`}>
                            <div className="flex justify-between items-start mb-2">
                              <p className="font-medium text-sm">{msg.subject}</p>
                              <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                            </div>
                            <p className="text-sm whitespace-pre-line">{msg.message}</p>
                            {msg.read && (
                              <p className="text-xs text-green-600 mt-2">✓ Gelesen am {formatDate(msg.read_at)}</p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Wählen Sie einen Verkäufer aus der Liste</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zahlung erfassen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Betrag (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Zahlungsart</Label>
              <Select
                value={paymentData.payment_method}
                onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Überweisung</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="cash">Bar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referenz/Verwendungszweck (optional)</Label>
              <Input
                value={paymentData.reference}
                onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                placeholder="z.B. Transaktionsnummer"
              />
            </div>
            <div>
              <Label>Notizen (optional)</Label>
              <Textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder="Interne Notizen..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowPaymentDialog(false)} className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0">Abbrechen</Button>
            <Button onClick={handleRecordPayment} className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">Zahlung erfassen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#F5EDE4' }}>
          <DialogHeader>
            <DialogTitle className="text-[#5D4E3C]">Nachricht an Verkäufer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#5D4E3C]">Nachrichtentyp</Label>
              <Select
                value={messageData.message_type}
                onValueChange={(value) => setMessageData({ ...messageData, message_type: value })}
              >
                <SelectTrigger className="rounded-none border-[#D4C4B0]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Allgemein</SelectItem>
                  <SelectItem value="reminder">Erinnerung</SelectItem>
                  <SelectItem value="warning">Warnung</SelectItem>
                  <SelectItem value="final_notice">Letzte Mahnung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#5D4E3C]">Betreff</Label>
              <Input
                value={messageData.subject}
                onChange={(e) => setMessageData({ ...messageData, subject: e.target.value })}
                placeholder="Betreff der Nachricht"
                className="rounded-none border-[#D4C4B0]"
              />
            </div>
            <div>
              <Label className="text-[#5D4E3C]">Nachricht</Label>
              <Textarea
                value={messageData.message}
                onChange={(e) => setMessageData({ ...messageData, message: e.target.value })}
                placeholder="Ihre Nachricht an den Verkäufer..."
                rows={6}
                className="rounded-none border-[#D4C4B0]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowMessageDialog(false)} className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0">Abbrechen</Button>
            <Button onClick={handleSendMessage} className="rounded-none bg-[#E3F2FD] text-[#2196F3] hover:bg-[#BBDEFB] border-0">
              <Send className="h-4 w-4 mr-2" />
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionTracking;
